import React, { FC, useEffect, useState } from "react";
import axios from "axios";
import { Formik, Field, Form } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { Container, Row, Col, Card, Button } from "react-bootstrap";
import ConfirmationModal from "../shared/ConfirmationModal";
import isEqual from "lodash/isEqual";
import NewconfirmationModal from "../shared/NewconfirmationModal";
import AllFailureModal from "../shared/AllFailureModal";

const TestimonialEdit: FC = () => {
  const { id } = useParams();
  const initialTestimonialState = {
    testimonial_Id: id,
    author_name: "",
    author_designation: "",
    testimonial_text: "",
    rating: "",
    author_image: "",
  };

  const [updateData, setUpdateData] = useState(initialTestimonialState);
  const [modalShow, setModalShow] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [showFailureModal, setShowFailureModal] = useState(false);
  const [failureMessage, setFailureMessage] = useState("");
  const [modalMessage, setModalMessage] = useState("");
  const [pendingValues, setPendingValues] = useState<any>(null);
  const navigate = useNavigate();
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [existingImage, setExistingImage] = useState<string | null>(null);

  useEffect(() => {
    const fetchTestimonialById = async () => {
      try {
        const url = `${process.env.REACT_APP_API_URL}testimonials/showtestimonial/${id}`;
        const response = await axios.get(url);
        if (response.data.length > 0) {
          setUpdateData(response.data[0]);
          setExistingImage(response.data[0].author_image);
        }
      } catch (error) {
        console.error("Error fetching testimonial:", error);
      }
    };

    fetchTestimonialById();
  }, [id]);

  const updateTestimonialApi = async (data: any, imageFile: File | null) => {
    const url = `${process.env.REACT_APP_API_URL}testimonials/update/${id}`;
    const formData = new FormData();

    formData.append("author_name", data.author_name);
    formData.append("author_designation", data.author_designation);
    formData.append("testimonial_text", data.testimonial_text);
    formData.append("rating", data.rating);

    if (imageFile) {
      formData.append("author_image", imageFile);
    }
    const token = localStorage.getItem("jwttoken");
    if (!token) {
        setFailureMessage("Unauthorized: Please login first.");
        setShowFailureModal(true);
        return;
    }

        await axios.put(url, formData, {
            headers: { 
                "Content-Type": "multipart/form-data",
                "x-access-token": token,
            },
        });
};

  const handleUpdateConfirmation = async (isConfirmed: boolean) => {
    setModalShow(false);
    if (isConfirmed && pendingValues) {
      await updateTestimonialApi(pendingValues, selectedImage);
      setSuccessMessage("Updated successfully!");
      setShowSuccessModal(true);
      setPendingValues(null);
      setTimeout(() => {
        navigate("/testimonials");
      }, 3000);
    } else {
      setFailureMessage("Failed to update the User.");
      setShowFailureModal(true);
    }
  };

  return (
    <Container className="mt-5">
      <Row className="justify-content-center">
        <Col md={8}>
          <Card className="shadow">
            <Card.Header className="bg-info text-white text-center">
              <h4>Update Testimonial</h4>
            </Card.Header>
            <Card.Body>
              <ConfirmationModal
                show={modalShow}
                modalMessage={modalMessage}
                onHide={() => setModalShow(false)}
                confirmation={handleUpdateConfirmation}
                operationType="Update"
              />
              <NewconfirmationModal
                show={showSuccessModal}
                onHide={() => setShowSuccessModal(false)}
                modalMessage={successMessage}
                operationType="OK"
                timeoutDuration={3000}
              />
              <AllFailureModal
                show={showFailureModal}
                onHide={() => setShowFailureModal(false)}
                modalMessage={failureMessage}
                operationType="OK"
                timeoutDuration={5000}
              />

              <Formik
                enableReinitialize={true}
                initialValues={updateData}
                onSubmit={async (values) => {
                  if (isEqual(values, updateData) && !selectedImage) {
                    setSuccessMessage("No changes were made.");
                    setShowSuccessModal(true);
                  } else {
                    try {
                      setPendingValues(values);
                      setModalMessage("Are you sure you want to update the user?");
                      setModalShow(true);
                    } catch (error: any) {
                      if(error.response.status===400){
                        setFailureMessage("Both Email and Mobile already exists")
                      }
                      else if(error.response.status===401){
                        setFailureMessage("Email already exists")
                      }
                      else if(error.response.status===402){
                        setFailureMessage("Mobile already exists")
                      }else{
                        setFailureMessage("Network Error.Failed to Update");
                      }
                      // setFailureMessage(error.response?.data?.message || "An error occurred. Please try again.");
                      setShowFailureModal(true);
                    }
                  }
                }}
              >
                {({ setFieldValue }) => (
                  <Form>
                    <Field hidden type="number" name="testimonial_Id" />

                    <Row className="mb-3">
                      <Col md={6}>
                        <label htmlFor="author_name" className="form-label">
                          Author Name:
                        </label>
                        <Field
                          name="author_name"
                          type="text"
                          className="form-control"
                          required
                        />
                      </Col>

                      <Col md={6}>
                        <label
                          htmlFor="author_designation"
                          className="form-label"
                        >
                          Designation:
                        </label>
                        <Field
                          name="author_designation"
                          type="text"
                          className="form-control"
                          required
                        />
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col md={12}>
                        <label
                          htmlFor="testimonial_text"
                          className="form-label"
                        >
                          Testimonial:
                        </label>
                        <Field
                          as="textarea"
                          name="testimonial_text"
                          className="form-control"
                          required
                        />
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col md={6}>
                        <label htmlFor="rating" className="form-label">
                          Rating (1-5):
                        </label>
                        <Field
                          name="rating"
                          type="number"
                          min="1"
                          max="5"
                          className="form-control"
                          required
                        />
                      </Col>
                      <Col md={6}>
                        <label htmlFor="author_image" className="form-label">
                          Upload New Image (Optional):
                        </label>
                        <input
                          id="author_image"
                          name="author_image"
                          type="file"
                          className="form-control"
                          accept="image/*"
                          onChange={(event) => {
                            setFieldValue("author_image", event.currentTarget.files?.[0] || null);
                            setSelectedImage(event.currentTarget.files?.[0] || null);
                          }}
                        />
                        {existingImage && !selectedImage && (
                          <small>Current Image: {existingImage}</small>
                        )}
                        {!selectedImage && !existingImage && (
                          <small>CHOOSE FILE: No file chosen</small>
                        )}
                        {selectedImage && <small>CHOOSE FILE: {selectedImage.name}</small>}
                      </Col>
                    </Row>

                    <Row className="mt-4">
                      <Col className="text-center">
                        <Button type="submit" variant="success">
                          Update Testimonial
                        </Button>
                        <Button
                          variant="danger"
                          className="mx-2"
                          onClick={() => navigate("/testimonials")}
                        >
                          Go Back
                        </Button>
                      </Col>
                    </Row>
                  </Form>
                )}
              </Formik>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default TestimonialEdit;

